import { useEffect, useRef } from 'react';
import { Badge, Box, Flex, HStack, Icon, IconButton, Image, Text } from '@chakra-ui/react';
import { FiAlertCircle, FiRotateCcw } from 'react-icons/fi';
import { resolveImageUrl } from '../../services/api';
import { formatTime } from '../../utils/dateUtils';

// 長按多久算「長按」。450ms 是手機 App 慣例區間（LINE/IG 約 400~500ms）：
// 再短會跟「捲動時手指先按住」誤觸，再長使用者會以為沒反應而放開。
const LONG_PRESS_MS = 450;

// 一般文字 / 圖片訊息氣泡（左右對齊）。comm_book 型訊息不走這裡，改由 CommBookCard 整條渲染。
// 樂觀更新（useOptimisticSend）會傳入帶 `_status` 的暫時訊息：
//   'sending' → **外觀與已送出的訊息完全相同**，只有時間欄先留白（等真訊息回來才填）
//   'failed'  → 時間位置改成紅色驚嘆號，點一下重送（比照 LINE）
// 送出中不做任何視覺提示是刻意的（2026-08-13 user 要求）：絕大多數訊息都會順利送達，
// 為它們加上半透明＋「傳送中」字樣，等於每送一則就閃一次多餘的狀態，反而顯得卡。
// 後端回來的真訊息沒有 `_status`，以下分支全部不成立＝渲染結果與改動前逐像素相同。
//
// onRecall（optional）：傳入才會出現「收回」的入口（長按／hover）。刻意做成 opt-in——
// 這顆氣泡被老師 PWA、家長 App、桌面對話頁三處共用，不傳的那些端一個像素都不會變。
// 元件本身只負責「使用者表達了要收回這一則」，確認對話框與 API 由頁面層的
// RecallConfirmDialog 處理（對話框一頁掛一個就好，不必每顆氣泡各掛一個）。
//
// showSenderName（optional，預設 true）：這一則是不是「同一個人的連續訊息中的第一則」。
// 由 MessageList 算好傳進來——只有它看得到整串訊息的前後關係，氣泡自己只認得自己那一則。
// 預設 true 是為了向後相容：群發預覽頁（TeacherBroadcastPage／MobileBroadcast）直接渲染
// 單一顆氣泡、不經過 MessageList，不傳這個參數時行為與加這段之前完全相同。
//
// orgView（optional，預設 false）：以「機構視角」而不是「我的視角」決定左右。見下方 alignRight。
export default function MessageBubble({ message, isMine, showSenderName = true, orgView = false, onImageClick, onRetry, onRecall, onContextMenu }) {
    const {
        content_type: contentType, text, image_url: imageUrl,
        sender_name: senderName, created_at: createdAt, is_broadcast: isBroadcast,
        sender_role: senderRole,
    } = message;
    // 【左右方向：isMine 是「誰的權限」，alignRight 是「怎麼呈現」，這次把兩者拆開】
    // 機構對家長是**單一窗口**（LINE 官方帳號的模型）：家長看到的是「補習班」，不是某位
    // 老師個人。所以機構端自己在看對話時，方向也該照這個模型分——家長靠左、機構一律靠右。
    // 照原本的 isMine 排會出事的是管理員：他幾乎不發言，於是老師的訊息和家長的訊息**全部
    // 擠在左邊**（兩者都不是「我」發的），一眼分不出哪句是誰說的（user 2026-08-19 實機回報）。
    // orgView 不傳＝維持原本的「我發的靠右」，家長 App 那一端因此逐字不變。
    //
    // 依據 sender_role（'parent' / 'teacher' / 'admin'，見後端 chat.py _sender_role_for），
    // 不是 sender_id：機構端有多人，要分的是「哪一邊」而不是「哪一個人」。
    // 後端該欄位 nullable=False 且每條回傳管道都會帶（_message_dict），樂觀送出的暫時訊息
    // 也自己填了（useOptimisticSend.roleOf），所以這裡不會拿到 undefined。
    const alignRight = orgView ? senderRole !== 'parent' : isMine;
    const isSending = message._status === 'sending';
    const isFailed = message._status === 'failed';
    // 舊後端不回傳這個欄位 → undefined → `=== true` 為 false → 以下全部走原本的分支，
    // 渲染結果與加這段之前逐像素相同。
    const isRecalled = message.is_recalled === true;
    // 自己的訊息不標名字（右側對齊已經說明了是誰）；對方的訊息**一定要**標——一位學生
    // 可以綁多位家長（正式站 317 位有綁家長的學生中 208 位綁 2 位以上），而一條對話是
    // (學生×班級)、兩位家長共用同一條，不標名字老師就分不出是爸爸還是媽媽說的
    //（user 2026-08-18 回報）。家長端同理：一個班可能有多位老師。
    // 連續訊息只標第一則，理由見 MessageList 的 startsRun。
    // 已收回的墓碑**照樣標名字**：後端收回時清的是 text/image_url，sender_name 是外殼、留著的
    // （見 chat.py _message_dict）。「誰收回了一則訊息」正是這種時候最需要知道的事，尤其兩位
    // 家長共用一條對話時；而且墓碑若不標名字，它後面那一則同一人的訊息會被誤判成「換人了」。
    // 機構視角下**兩側都要標名字**（不再只標「不是我發的」那些）：右邊代表的是「機構」而不是
    // 「我」，沒有名字的右側氣泡等於在說「這是機構發的，但不知道是誰」——而機構端有多人，
    // 老師之間正需要知道是誰回的（user 2026-08-19 指定）。自己發的那些也一起標，否則
    // 「沒有名字＝是我」變成一條要背起來的隱藏規則，同事的名字有、自己的沒有反而更難讀。
    const showSender = showSenderName && !!senderName && (orgView || !isMine);
    // 舊後端不回傳這個欄位 → undefined → 不顯示徽章，行為與加這段之前完全相同
    const showHeadRow = showSender || isBroadcast;

    // 可收回的條件（與後端 DELETE /chat/messages/{id} 的規則對齊，但這裡只是「要不要顯示入口」，
    // 真正的把關一律在後端：24 小時時間窗前端不自己算，時鐘差一點就會顯示成能點卻打不成功／
    // 或明明還能收回卻不給點，後端已經會用 400 + 說明文字回覆）。
    // 樂觀訊息（sending/failed）沒有後端 id，收回無從打起；失敗的那則本來就用「重送」處理。
    // ⚠ 這裡刻意**維持 isMine**、不跟著改成 alignRight：收回是權限問題（後端只允許發送者
    // 本人收回），機構視角只換了呈現方式，不代表可以收回同事發的訊息。
    const canRecall = !!onRecall && isMine && !isRecalled && !isSending && !isFailed && contentType !== 'comm_book';

    // 長按觸發情境：
    //   onContextMenu 有傳 → 對任何可互動訊息（text/image，排除 comm_book、已收回、pending）開選單
    //   onContextMenu 未傳 → 舊行為（僅自己可收回的訊息才長按）
    const canContextMenu = !!onContextMenu && contentType !== 'comm_book' && !isRecalled && !isSending && !isFailed;
    const canLongPress = onContextMenu ? canContextMenu : canRecall;

    // 長按（手機）：計時器要能被捲動/放手/元件卸載中斷，否則手指按著捲畫面也會跳出收回確認。
    const pressTimerRef = useRef(null);
    // 這一輪長按是否已經觸發過（用來吃掉放開手指時瀏覽器補送的那個 click，見 handleTouchEnd）
    const pressFiredRef = useRef(false);
    const clearPress = () => {
        if (pressTimerRef.current) {
            clearTimeout(pressTimerRef.current);
            pressTimerRef.current = null;
        }
    };
    // 卸載時清掉未觸發的計時器（換對話／清單重繪時氣泡會被拆掉）。
    // 這裡刻意重寫一次清除邏輯而不是相依 clearPress：那個函式每次 render 都是新參考，
    // 放進相依陣列會變成「每 render 清一次計時器」＝長按期間只要有一則 WS 訊息進來就被取消。
    useEffect(() => () => {
        if (pressTimerRef.current) clearTimeout(pressTimerRef.current);
    }, []);
    const handleTouchStart = () => {
        if (!canLongPress) return;
        pressFiredRef.current = false;
        clearPress();
        pressTimerRef.current = setTimeout(() => {
            pressTimerRef.current = null;
            pressFiredRef.current = true;
            if (canContextMenu) {
                onContextMenu({ message, canRecall });
            } else {
                onRecall?.(message);
            }
        }, LONG_PRESS_MS);
    };
    const handleTouchEnd = (e) => {
        clearPress();
        // 長按已經把確認框叫出來之後才放手：這一下 touchend 若不擋掉，瀏覽器會在同一個座標
        // 補送一個相容用的 click，而那個位置此刻已經蓋著剛彈出的對話框——輕則點到遮罩把框
        // 關掉（使用者會覺得「長按沒反應」），重則正好點在「收回」上＝沒確認就收回。
        // onTouchStart/onTouchMove 在 React 是 passive listener（preventDefault 無效），
        // 只有 onTouchEnd 擋得住那個補送的 click，所以這段一定要放在這裡。
        if (pressFiredRef.current) {
            pressFiredRef.current = false;
            e.preventDefault();
        }
    };

    return (
        <Flex direction="column" align={alignRight ? 'flex-end' : 'flex-start'} mb={3}>
            {showHeadRow && (
                <HStack spacing={1} mb={0.5} mx={1}>
                    {/* 加粗是配合「只在換人時才出現」的：出現次數少了，這一行就得夠醒目才抓得住
                        眼睛。11px 常規字重在中間調畫布(#8A9A7B)上很容易被當成背景紋理略過，
                        而它現在是「這串話換人了」的唯一提示。onCanvas.400 對比 5.22（達 AA）。 */}
                    {showSender && (
                        <Text fontSize="11px" fontWeight="semibold" color="onCanvas.400">{senderName}</Text>
                    )}
                    {/* 群發標示：讓家長一眼看出「這是發給全班的」，而不是老師單獨傳給他的
                        —— 否則家長可能會在這條對話裡回覆比較私人的內容。
                        老師端看到同一個徽章也有用：可以分辨哪些是自己群發出去的。 */}
                    {isBroadcast && (
                        <Badge colorScheme="brand" variant="subtle" fontSize="9px" borderRadius="sm">全班</Badge>
                    )}
                </HStack>
            )}
            {/* position/role 都只在「這一則可以收回」時才加：不可收回的氣泡（含家長端全部氣泡）
                連 stacking context 都不會多一層，渲染與加這段之前逐字相同。 */}
            <Flex
                align="flex-end"
                gap={1.5}
                maxW="82%"
                position={canRecall ? 'relative' : undefined}
                role={canRecall ? 'group' : undefined}
            >
                {/* 桌面的收回入口：hover 才浮出，且用絕對定位掛在氣泡左外側——若照一般 flex 子元素
                    排進去，這顆按鈕平時就會佔掉版面寬度，等於把所有自己發的氣泡往左推一截。
                    觸控裝置上改用長按（下方 onTouchStart），所以這裡把它藏起來：不然 hover 樣式
                    在手機點過一次後會黏著不消，變成畫面上多一顆莫名其妙的按鈕。 */}
                {canRecall && (
                    <IconButton
                        aria-label="收回訊息"
                        icon={<Icon as={FiRotateCcw} />}
                        size="xs"
                        variant="ghost"
                        // 這顆按鈕是**直接壓在畫布上**的（不在氣泡裡），所以用 onCanvas 階：
                        // 手機畫布是霧灰橄欖 #8A9A7B，neutral.600 在它上面只有 2.96 對比、
                        // 連 UI 元件的 3.0 門檻都構不到；onCanvas.500 是 5.83。
                        // 桌面（neutral.50 底）上這個近黑階只會更清楚，兩邊共用同一個值即可。
                        color="onCanvas.500"
                        position="absolute"
                        right="100%"
                        bottom="2px"
                        mr={1}
                        opacity={0}
                        _groupHover={{ opacity: 1 }}
                        _focusVisible={{ opacity: 1 }}
                        onClick={() => onRecall?.(message)}
                        sx={{ '@media (hover: none)': { display: 'none' } }}
                    />
                )}

                {/* 已讀回條（單向）：後端只在非家長身分取訊息時才附 read_by_parent，
                    所以家長端這個欄位永遠是 undefined、不會渲染。老師看到「已讀」＝家長真的看過。
                    已收回的訊息不顯示已讀：那句「已讀」指的是現在已經不存在的內容，留著只會讓人
                    以為對方看到的是墓碑以外的東西。
                    改看 alignRight 之後，機構視角下**同事發的訊息也會顯示已讀**——這正是要的：
                    這個標記回答的是「家長看到機構的回覆了沒」，跟回覆的人是誰無關（管理員最需要
                    的就是這個）。家長自己發的那些不受影響：後端本來就不對 sender_role='parent'
                    的訊息算已讀回條（見 chat.py list_messages），且它們在機構視角下靠左。 */}
                {alignRight && !isFailed && !isSending && !isRecalled && message.read_by_parent && (
                    <Text fontSize="9px" color="onCanvas.500" fontWeight="bold" mb={0.5} flexShrink={0}>已讀</Text>
                )}
                {alignRight && (isFailed ? (
                    <Box
                        as="button"
                        type="button"
                        aria-label="傳送失敗，點一下重新傳送"
                        onClick={() => onRetry?.(message.id)}
                        flexShrink={0}
                        mb={0.5}
                        lineHeight={0}
                    >
                        <Icon as={FiAlertCircle} color="red.500" boxSize="18px" />
                    </Box>
                ) : (
                    // 傳送中：時間位置留白（不寫「傳送中」）。比照 LINE——送出瞬間氣泡就跟
                    // 已送出的長得一樣，順利送達時使用者從頭到尾不會看到任何額外狀態文字，
                    // 只有真的沒送出去才用紅色驚嘆號提醒。時間等後端回傳真訊息才填上。
                    <Text fontSize="10px" color="onCanvas.300" mb={0.5} flexShrink={0}>
                        {isSending ? '' : formatTime(createdAt)}
                    </Text>
                ))}

                <Box
                    // 已收回：兩側都退成同一種中性樣式（不再分「我的綠／對方的白」）。
                    // 墓碑不是內容，用發話者顏色只會讓它在對話裡繼續搶眼；虛線邊框則是在說
                    // 「這裡本來有東西、已經沒了」。左右對齊維持不變，仍看得出是誰收回的。
                    bg={isRecalled ? 'neutral.100' : (alignRight ? 'brand.500' : 'white')}
                    color={isRecalled ? 'neutral.500' : (alignRight ? 'white' : 'neutral.800')}
                    border={isRecalled ? '1px dashed' : (alignRight ? 'none' : '1px solid')}
                    borderColor={isRecalled ? 'neutral.300' : 'neutral.200'}
                    borderRadius="16px"
                    borderBottomRightRadius={alignRight ? '4px' : '16px'}
                    borderBottomLeftRadius={!alignRight ? '4px' : '16px'}
                    // 收回後 image_url 一定是 null，所以圖片專用的窄內距要跟著退回文字內距，
                    // 否則墓碑文字會貼著邊框。
                    px={contentType === 'image' && !isRecalled ? 1.5 : 3}
                    py={contentType === 'image' && !isRecalled ? 1.5 : 2}
                    // 對方氣泡（米白）在中間調畫布上的亮度差只有 2.79，未達 UI 輪廓門檻 3.0。
                    // 這不是氣泡顏色選錯——**任何淺色填色在此畫布上都不可能超過 3.0**
                    //（純白剛好等於 3.00），是畫布亮度決定的上限。既有的 neutral.200 邊框
                    // 在綠底上幾乎看不見（1.4），所以改用較實的陰影來界定邊緣；
                    // 其餘的分離感由色相差（暖米 vs 帶綠灰）承擔，那是 WCAG 的亮度公式量不到的。
                    // 墓碑同樣需要這層陰影（它也是淺色填色壓在同一塊畫布上），只是邊框改虛線。
                    boxShadow={alignRight && !isRecalled ? 'sm' : '0 1px 3px rgba(28, 25, 20, 0.20)'}
                    onTouchStart={canLongPress ? handleTouchStart : undefined}
                    onTouchEnd={canLongPress ? handleTouchEnd : undefined}
                    onTouchMove={canLongPress ? clearPress : undefined}
                    onTouchCancel={canLongPress ? clearPress : undefined}
                    // 長按預設會觸發 iOS 的文字選取與放大鏡（callout），跟功能選單搶同一個手勢。
                    // 凡是有長按功能的氣泡都關掉系統 callout，讓選單優先。
                    sx={canLongPress ? { WebkitTouchCallout: 'none', userSelect: 'none' } : undefined}
                >
                    {isRecalled ? (
                        // 後端在回收時就把 text / image_url 清成 null（連 WS 廣播都是清空後的版本），
                        // 所以這裡不是「把內容藏起來」而是本來就沒有內容可以顯示。
                        <Text fontSize="sm" fontStyle="italic">訊息已收回</Text>
                    ) : contentType === 'image' ? (
                        <Image
                            src={resolveImageUrl(imageUrl)}
                            onClick={() => onImageClick?.(resolveImageUrl(imageUrl))}
                            maxW="200px"
                            maxH="240px"
                            objectFit="cover"
                            borderRadius="12px"
                            cursor="pointer"
                        />
                    ) : (
                        <Text fontSize="sm" whiteSpace="pre-wrap" wordBreak="break-word">{text}</Text>
                    )}
                </Box>

                {!alignRight && <Text fontSize="10px" color="onCanvas.300" mb={0.5} flexShrink={0}>{formatTime(createdAt)}</Text>}
            </Flex>
        </Flex>
    );
}
